import { Skeleton } from '@src/components/ui/skeleton'

export type ChartSkeletonVariant = 'horizontal' | 'vertical' | 'graph'

const LEGEND_ITEMS = 3

const SkeletonLegend = () => (
  <div className="mt-4 flex justify-center gap-4">
    {Array.from({ length: LEGEND_ITEMS }).map((_, i) => (
      <div key={i} className="flex items-center gap-1.5">
        <Skeleton className="size-2.5 rounded-full" />
        <Skeleton className="h-2.5 w-10" />
      </div>
    ))}
  </div>
)

const HorizontalBarSkeleton = () => (
  <div className="space-y-3 p-1">
    <Skeleton className="h-7 w-3/4" />
    <Skeleton className="h-7 w-full" />
    <Skeleton className="h-7 w-5/6" />
    <Skeleton className="h-7 w-full" />
    <Skeleton className="h-7 w-2/3" />
    <Skeleton className="h-7 w-full" />
  </div>
)

const VERTICAL_HEIGHTS = [60, 85, 45, 95, 70, 55, 80]

const VerticalBarSkeleton = () => (
  <div className="space-y-1.5">
    <div className="flex h-64 items-end justify-between gap-1.5">
      {VERTICAL_HEIGHTS.map((height, i) => (
        <Skeleton key={i} className="flex-1 rounded-b-none" style={{ height: `${height}%` }} />
      ))}
    </div>
    <div className="flex justify-between gap-1.5">
      {VERTICAL_HEIGHTS.map((_, i) => (
        <Skeleton key={i} className="h-2.5 flex-1" />
      ))}
    </div>
    <SkeletonLegend />
  </div>
)

const GRAPH_POINTS = [80, 65, 70, 45, 50, 30, 40, 25, 35, 20, 28]
const GRAPH_X_LABELS = 5

const GraphSkeleton = () => {
  const width = 200
  const height = 80
  const points = GRAPH_POINTS.map((y, i) => `${(i / (GRAPH_POINTS.length - 1)) * width},${y}`).join(
    ' ',
  )

  return (
    <div className="space-y-1.5">
      <svg viewBox={`0 0 ${width} ${height + 10}`} className="w-full" aria-hidden>
        <polyline
          points={points}
          fill="none"
          className="stroke-muted"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="flex justify-between">
        {Array.from({ length: GRAPH_X_LABELS }).map((_, i) => (
          <Skeleton key={i} className="h-2.5 w-6" />
        ))}
      </div>
      <SkeletonLegend />
    </div>
  )
}

interface ChartSkeletonProps {
  variant?: ChartSkeletonVariant
}

const ChartSkeleton = ({ variant = 'horizontal' }: ChartSkeletonProps) => {
  if (variant === 'vertical') return <VerticalBarSkeleton />
  if (variant === 'graph') return <GraphSkeleton />
  return <HorizontalBarSkeleton />
}

export default ChartSkeleton
